import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Download, Loader2, RotateCcw, Share2, X } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/shared/components/ui/alert-dialog';
import { useAuth } from '@/features/authentication/hooks/useAuth';
import { useGuestSignupModal } from '@/features/pre-login/context/GuestSignupModalContext';
import { downloadCarePlanPdf } from '@/features/chatbot/api/carePlanPdfApi';
import { ShareCareGuideModal } from './ShareCareGuideModal';
import { startNewCarePlan } from '../utils/navigatorSession';

type CareSeekerResultsActionsProps = {
  sessionId?: string;
  /** Care-guide label used as the share modal title. */
  planLabel: string;
  /**
   * Matchmaking still running. Both actions are built server-side from the
   * stored care plan, which is only persisted once the run finishes — sharing
   * or downloading now would hand over a silently incomplete guide.
   */
  isMatching?: boolean;
};

const outlinePillClass =
  'h-9 gap-2 rounded-full border-sage bg-transparent px-4 text-sm font-medium text-sage transition-colors duration-150 ease-out hover:bg-sage-bg hover:text-sage focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-emphasis disabled:opacity-60';

/**
 * Results-page action bar for the care-seeker / guest care guide. Mirrors the FP
 * ResultsActionsBar (amber Share + sage Download) and keeps the care-seeker
 * Restart action. Account-only actions (Share, Download hit JWT-guarded
 * endpoints) pop the guest signup modal instead of issuing a doomed request.
 */
export const CareSeekerResultsActions = ({
  sessionId,
  planLabel,
  isMatching = false,
}: CareSeekerResultsActionsProps) => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { openModal: openGuestSignupModal } = useGuestSignupModal();
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  const [isShareOpen, setIsShareOpen] = useState(false);
  const [isRestartConfirmOpen, setIsRestartConfirmOpen] = useState(false);

  const gateGuest = (triggerEntityId: string) =>
    openGuestSignupModal({ trigger: 'action_button_click', triggerEntityId });

  const handleShare = () => {
    if (!sessionId) return;
    if (!isAuthenticated) {
      gateGuest('care_plan_share');
      return;
    }
    setIsShareOpen(true);
  };

  const handleDownloadCarePlan = async () => {
    if (!sessionId || isDownloadingPdf) return;
    if (!isAuthenticated) {
      gateGuest('care_plan_pdf');
      return;
    }
    setIsDownloadingPdf(true);
    try {
      await downloadCarePlanPdf(sessionId);
    } catch (err) {
      console.error('Failed to download care plan PDF', err);
    } finally {
      setIsDownloadingPdf(false);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Button
        type="button"
        onClick={handleShare}
        disabled={!sessionId || isMatching}
        className="h-9 gap-2 rounded-full bg-amber-200 px-4 text-sm font-medium text-amber-900 transition duration-150 ease-out hover:bg-amber-300 hover:shadow-sm active:scale-[0.98] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-emphasis disabled:opacity-60 motion-reduce:transition-none motion-reduce:active:scale-100"
      >
        <Share2 className="h-3.5 w-3.5" aria-hidden />
        Share care guide
      </Button>

      <Button
        type="button"
        variant="outline"
        onClick={() => void handleDownloadCarePlan()}
        disabled={!sessionId || isDownloadingPdf || isMatching}
        className={outlinePillClass}
      >
        {isDownloadingPdf ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
        ) : (
          <Download className="h-3.5 w-3.5" aria-hidden />
        )}
        {isDownloadingPdf || isMatching ? 'Preparing care guide…' : 'Download care guide'}
      </Button>

      {/* Restart guide: confirm first (prevents a misclick from wiping the
          current guide view), then clear the resume state and route to the
          Navigator chat (blank Q&A). The X and "Keep my guide" both dismiss
          without restarting; "Keep my guide" (green) is the emphasized safe
          choice and "Yes, restart" (gray) is played down. */}
      <AlertDialog open={isRestartConfirmOpen} onOpenChange={setIsRestartConfirmOpen}>
        <AlertDialogTrigger asChild>
          <Button type="button" variant="outline" className={outlinePillClass}>
            <RotateCcw className="h-3.5 w-3.5" aria-hidden />
            Restart guide
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <button
            type="button"
            onClick={() => setIsRestartConfirmOpen(false)}
            aria-label="Close"
            className="absolute right-4 top-4 rounded-md p-1 text-gray-400 transition-colors hover:text-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
          <AlertDialogHeader>
            <AlertDialogTitle>Restart your care guide?</AlertDialogTitle>
            <AlertDialogDescription>
              This sends you back to the Navigator to answer the questions again and build a new
              guide from scratch.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {/* Cancel -> Action order (matches the app's other AlertDialogs) so the
              safe choice is first in tab order; color carries the emphasis. */}
          <AlertDialogFooter>
            <AlertDialogCancel className="border-0 bg-[#026857] text-white hover:bg-[#025246] focus:ring-[#026857]">
              Keep my guide
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => startNewCarePlan(navigate)}
              className="bg-gray-200 text-gray-800 hover:bg-gray-300 focus:ring-gray-400"
            >
              Yes, restart
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {sessionId && (
        <ShareCareGuideModal
          open={isShareOpen}
          sessionId={sessionId}
          planLabel={planLabel}
          onClose={() => setIsShareOpen(false)}
        />
      )}
    </div>
  );
};
